import { useEffect, useState } from 'react'
import type { Bar } from '../lib/types'
import { seriesOf, seriesStyle } from '../lib/series'
import { Bars } from './Results'
import { Counter, LiveDot } from './Bits'

/**
 * Макет эфира для первого экрана главной.
 *
 * Сервис про зрелище, и объяснять его словами бессмысленно: человек должен за
 * три секунды увидеть, что происходит на стене и что — в руке. Поэтому здесь не
 * картинка, а те же компоненты, что на настоящем проекторе и телефоне; голоса
 * идут по сценарию, без сервера.
 *
 * Весь блок скрыт от скринридера: это иллюстрация, а не содержимое, и цифры
 * в ней ничего не значат.
 */

const QUESTION = 'Что улучшаем в первую очередь?'
const OPTIONS = ['Скорость', 'Интерфейс', 'Интеграции', 'Мобильное приложение']
/** Перевес как в жизни: один явный лидер, один почти без голосов. */
const WEIGHTS = [6, 3, 2, 1]
const HALL = 48
/** Какой вариант «нажимает» телефон — второй, чтобы цвет отличался от лидера. */
const PHONE_PICK = 1

/** Заполненное распределение: для тех, у кого движение отключено, и как финал цикла. */
const FULL = distribute(HALL)

function distribute(total: number): number[] {
  const sum = WEIGHTS.reduce((a, b) => a + b, 0)
  const counts = WEIGHTS.map((w) => Math.floor((total * w) / sum))
  // Остаток от округления отдаём лидеру, чтобы сумма сходилась с числом ответивших.
  counts[0] += total - counts.reduce((a, b) => a + b, 0)
  return counts
}

export default function HeroDemo() {
  const [still] = useState(
    () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false,
  )
  const [answered, setAnswered] = useState(still ? HALL : 0)

  useEffect(() => {
    if (still) return
    let timer: number
    // Счёт ведём в замыкании, а не в функции обновления состояния: та обязана
    // быть чистой, а здесь каждый шаг заводит следующий таймер.
    let current = 0

    const step = () => {
      if (current >= HALL) {
        current = 0
        setAnswered(0)
        timer = window.setTimeout(step, 1400)
        return
      }
      current = Math.min(HALL, current + 1 + Math.floor(Math.random() * 3))
      setAnswered(current)
      // Зал ответил — держим результат подольше, потом начинаем заново.
      timer = window.setTimeout(step, current >= HALL ? 3600 : 260 + Math.random() * 420)
    }

    timer = window.setTimeout(step, 900)
    return () => window.clearTimeout(timer)
  }, [still])

  const counts = answered >= HALL ? FULL : distribute(answered)
  const bars: Bar[] = OPTIONS.map((text, index) => ({
    optionId: String(index),
    text,
    count: counts[index],
    correct: null,
  }))
  // Телефон «отвечает» не сразу: сначала видно чистый экран с вариантами.
  const picked = answered > 6

  return (
    <div className="demo" aria-hidden>
      <div className="demo-screen">
        <div className="demo-head">
          <LiveDot />
          <div className="col center" style={{ gap: 2 }}>
            <span className="eyebrow" style={{ fontSize: 11 }}>
              ответили
            </span>
            <Counter value={answered} className="stat" />
          </div>
        </div>
        <div className="stage-title">{QUESTION}</div>
        <Bars bars={bars} revealed={false} />
      </div>

      <div className="demo-phone">
        <div className="demo-phone-title">{QUESTION}</div>
        {OPTIONS.map((text, index) => (
          <div
            key={text}
            className={`option${picked && index === PHONE_PICK ? ' option-picked' : ''}`}
            style={seriesStyle(index)}
          >
            <span className="option-mark">{seriesOf(index).letter}</span>
            <span>{text}</span>
          </div>
        ))}
      </div>
    </div>
  )
}
